// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type FetchArgs,
  type BaseQueryEnhancer,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { type SerializedError } from '@reduxjs/toolkit';
import { Mutex } from 'async-mutex';
import { produce } from 'immer';
import { isDefined } from '@shared/utils';
import { authApi } from '@shared/data-access/api/auth0/auth-api';
import { authSlice } from '@shared/data-access/slices/auth';
import {
  selectToken,
  selectRefreshToken,
  selectExpirationDate,
  selectAuthUser,
} from '@shared/data-access/slices/auth';
import { type RootAuthState } from '@shared/data-access/slices/auth/auth-state';
import { type Auth0TokenErrorCode } from '@shared/data-access/types/auth0-types';
import { environment } from '@shared/environment';

const AUTH0_SESSION_INVALID_ERRORS: Auth0TokenErrorCode[] = [
  'invalid_grant',
  'expired_token',
  'login_required',
  'invalid_request',
];

const getRefreshErrorCode = (err: unknown): string | undefined => {
  if (err && typeof err === 'object' && 'data' in err) {
    return (err as { data?: { error?: string } }).data?.error;
  }
  return undefined;
};

const isRefreshSessionInvalid = (err: unknown): boolean => {
  const code = getRefreshErrorCode(err);
  return (
    typeof code === 'string' &&
    AUTH0_SESSION_INVALID_ERRORS.includes(code as Auth0TokenErrorCode)
  );
};

export interface AuthTokenQueryOptions {
  authTokenUsage?: 'header' | `query-${'user' | 'email'}` | false;
}

const AUTH_QUERY_PARAM_KEY = new Map([
  ['query-user', 'user'],
  ['query-email', 'email'],
] as const);

const is401Error = (result: any): boolean =>
  typeof result?.error !== 'undefined' &&
  (result?.error as FetchBaseQueryError).status === 401;

const refreshMutex = new Mutex();

const EXPIRATION_BUFFER_MS = 60 * 1000;

const isTokenExpiredOrExpiring = (
  state: RootAuthState,
  tokenUsage: string | false,
): boolean => {
  if (tokenUsage !== 'header') return false;
  const token = selectToken(state);
  const expiration = selectExpirationDate(state);
  if (token === null || expiration === null) return false;
  return Date.now() >= expiration - EXPIRATION_BUFFER_MS;
};

/**
 * Base query enhancer: authenticates API requests with token (Bearer header or
 * query param). Uses auth state `expiration_date` to refresh the token before it
 * expires (buffer 60s) instead of waiting for 401. Blocks requests while a refresh
 * is in progress; on 401 one request performs refresh, others wait and retry.
 * Prevents multiple refresh calls and unnecessary unauthorized errors.
 *
 * @see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-re-authorization-by-extending-fetchbasequery
 * @see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#preventing-multiple-unauthorized-errors
 */
export const authTokenQuery: BaseQueryEnhancer<
  FetchArgs,
  AuthTokenQueryOptions,
  AuthTokenQueryOptions
> =
  (baseQuery, config) =>
  async (args, api, extraOptions): Promise<any> => {
    const tokenUsage =
      extraOptions?.authTokenUsage ?? config?.authTokenUsage ?? false;

    const runRequest = async (overrides?: { token?: string }): Promise<any> => {
      const state = api.getState() as RootAuthState;
      const token = overrides?.token ?? selectToken(state);
      const user = selectAuthUser(state);
      const paramKey = AUTH_QUERY_PARAM_KEY.get(
        tokenUsage as 'query-user' | 'query-email',
      );

      switch (tokenUsage) {
        case 'header': {
          const request = produce(args ?? {}, (draft: FetchArgs) => {
            const headers = new Headers(draft?.headers as HeadersInit | undefined);

            if (token) headers.set('Authorization', `Bearer ${token}`);

            draft.headers = headers;
          });

          return baseQuery(request, api, extraOptions);
        }
        case 'query-email':
        case 'query-user': {
          const request = produce(args ?? {}, (draft: FetchArgs) => {
            const base = (draft.params ?? {}) as Record<string, unknown>;

            draft.params = produce(base, (paramDraft: Record<string, unknown>) => {
              if (isDefined(user?.email)) {
                paramDraft[paramKey as string] = user.email;
              }
            });

            if (Object.keys(draft.params).length === 0) delete draft.params;
          });

          return baseQuery(request, api, extraOptions);
        }
        default:
          return baseQuery(args, api, extraOptions);
      }
    };

    await refreshMutex.waitForUnlock();

    const state = api.getState() as RootAuthState;
    const refreshToken = selectRefreshToken(state);
    const expiredOrExpiring = isTokenExpiredOrExpiring(state, tokenUsage);

    if (expiredOrExpiring && refreshToken !== null && !refreshMutex.isLocked()) {
      const resultAfterRefresh = await refreshMutex.runExclusive(async () => {
        try {
          const payload = await api
            .dispatch(
              authApi.endpoints.refreshTokens.initiate({
                grant_type: 'refresh_token',
                client_id: environment.AUTH.CLIENT_ID!,
                refresh_token: refreshToken,
              }),
            )
            .unwrap();

          return runRequest({ token: payload.access_token });
        } catch (err) {
          if (isRefreshSessionInvalid(err)) {
            const serialized: SerializedError = (err as { error?: SerializedError })
              ?.error ?? {
              name: 'Auth0Error',
              message: 'Session invalid',
            };
            api.dispatch(authSlice.actions.setLoginError(serialized));
          }
          return runRequest();
        }
      });
      return resultAfterRefresh;
    }

    if (expiredOrExpiring && refreshToken !== null && refreshMutex.isLocked()) {
      await refreshMutex.waitForUnlock();
      return runRequest();
    }

    const result = await runRequest();

    if (!is401Error(result)) {
      return result;
    }

    if (refreshToken === null) {
      api.dispatch(
        authSlice.actions.setLoginError(
          (result as { error?: SerializedError }).error as SerializedError,
        ),
      );
      return result;
    }

    if (!refreshMutex.isLocked()) {
      const resultAfterRefresh = await refreshMutex.runExclusive(async () => {
        try {
          const payload = await api
            .dispatch(
              authApi.endpoints.refreshTokens.initiate({
                grant_type: 'refresh_token',
                client_id: environment.AUTH.CLIENT_ID!,
                refresh_token: refreshToken,
              }),
            )
            .unwrap();

          return runRequest({ token: payload.access_token });
        } catch (err) {
          if (isRefreshSessionInvalid(err)) {
            api.dispatch(
              authSlice.actions.setLoginError(
                (result as { error?: SerializedError }).error as SerializedError,
              ),
            );
          }
          return result;
        }
      });

      return resultAfterRefresh;
    }

    await refreshMutex.waitForUnlock();

    return runRequest();
  };
